import React, { useEffect, useState, useMemo } from 'react';
import { skillMatrixApi, associateDesignationApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Pencil, Check, X, Trash2, Plus, ChevronRight, ChevronLeft } from 'lucide-react';

/* Skills Bank, and the designation→skill mapping built on top of it.

   These two sections are what make the Skill Matrix configuration rather
   than code. Before them the pool could only be renamed: a genuinely new
   skill meant editing a constant in server.py and deploying. An admin now
   adds a skill here, maps it to a designation, and every associate holding
   that designation picks it up on their next assessment.

   Nothing in this file names a skill. The lists come from the bank, the
   designations from the associate designation master, and both are read at
   render time — which is the point. */

const CARD = 'bg-white border border-[#E6E9F0] rounded-[14px] p-6 sm:p-7 shadow-[0_1px_2px_rgba(16,36,104,0.04)]';
const LBL = 'text-xs font-semibold text-[#5A6377] mb-1 block';

/* ------------------------------------------------------------------ */
/* Skills Bank — the single source of truth for what a skill IS.       */
/* ------------------------------------------------------------------ */
export function SkillsBankCard() {
  const { hasPerm } = useAuth();
  const allowed = hasPerm('master.manage');
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState('');
  const [edit, setEdit] = useState(null);           // { key, name, max_marks, category }
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', max_marks: 4, method: 'quad', category: '' });
  const [busy, setBusy] = useState(false);

  const load = () => skillMatrixApi.pool()
    .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { if (allowed) load(); }, [allowed]);
  if (!allowed) return null;

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof ok === 'function' ? ok(r) : ok); load(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const add = () => {
    if (!draft.name.trim()) { toast.error('Skill name is required'); return; }
    run(() => skillMatrixApi.create({
      name: draft.name.trim(),
      // A practically-rated skill is out of 4 by construction — the
      // assessment square has four parts. Only a typed-total skill may
      // carry anything else.
      max_marks: draft.method === 'quad' ? 4 : Number(draft.max_marks || 0),
      method: draft.method,
      category: draft.category.trim() || null,
    }), 'Skill added to the bank').then((r) => {
      if (r) { setDraft({ name: '', max_marks: 4, method: 'quad', category: '' }); setAdding(false); }
    });
  };

  const remove = (s) => {
    // The server decides delete vs deactivate from the assessment count; the
    // confirmation says which it will be rather than threatening data loss
    // that will not happen.
    const msg = s.assessment_count
      ? `${s.name} is used by ${s.assessment_count} assessment(s).\n\nIt will be switched OFF, not deleted — existing assessments keep reading correctly, and it stops being offered on new ones.`
      : `Delete ${s.name}?\n\nNo assessment has ever scored it, so nothing is lost.`;
    if (!window.confirm(msg)) return;
    run(() => skillMatrixApi.remove(s.key),
        (r) => (r.data.deleted ? 'Skill deleted' : 'Skill switched off — history kept'));
  };

  const shown = (rows || []).filter((s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${s.name} ${s.category || ''} ${s.key}`.toLowerCase().includes(q);
  });

  return (
    <div className={CARD} data-testid="skills-bank-card">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div>
          <h2 className="text-[17px] font-semibold text-[#11182B] leading-tight">Skills Bank</h2>
          <p className="text-[12px] text-[#98A1B2] mt-0.5">
            {(rows || []).filter((s) => s.active).length} active · {(rows || []).length} total
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding((v) => !v)} data-testid="skill-add-toggle">
          <Plus className="w-4 h-4 mr-1" />{adding ? 'Cancel' : 'Add skill'}
        </Button>
      </div>
      <p className="text-[12px] text-[#98A1B2] mb-5">
        Each skill is assessed as <b>Rating</b> (pick a value on the square) or
        <b> Marking</b> (type a total out of a maximum). Change it any time — past
        assessments keep the method they were taken under.
      </p>

      {adding && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5 p-4 rounded-xl bg-[#F7F8FB] border border-[#EDF0F5]">
          <div className="sm:col-span-2">
            <Label className={LBL}>Skill name</Label>
            <Input value={draft.name} data-testid="skill-new-name"
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                   placeholder="e.g. Crane Operation" />
          </div>
          <div>
            <Label className={LBL}>Category</Label>
            <Input value={draft.category} data-testid="skill-new-category"
                   onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                   placeholder="optional" />
          </div>
          <div>
            <Label className={LBL}>Assessed as</Label>
            <select className="border border-[#E6E9F0] rounded-md text-sm px-2 py-2 w-full bg-white"
                    value={draft.method} data-testid="skill-new-method"
                    onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
              {/* Same two words everywhere — the row badge, this picker and
                  the edit control all say Rating / Marking. */}
              <option value="quad">Rating — pick 1–4 on the square</option>
              <option value="marks">Marking — type a total</option>
            </select>
          </div>
          {draft.method === 'marks' && (
            <div>
              <Label className={LBL}>Out of</Label>
              <Input type="number" min="1" value={draft.max_marks} data-testid="skill-new-max"
                     onChange={(e) => setDraft({ ...draft, max_marks: e.target.value })} />
            </div>
          )}
          <div className="sm:col-span-4 flex justify-end">
            <Button size="sm" onClick={add} disabled={busy} data-testid="skill-new-save">Add to bank</Button>
          </div>
        </div>
      )}

      {(rows || []).length > 6 && (
        <Input className="h-10 mb-3" value={query} data-testid="skill-search"
               onChange={(e) => setQuery(e.target.value)} placeholder="Search skills…" />
      )}

      <div className="divide-y divide-[#F0F2F6]">
        {rows && !shown.length && (
          <p className="py-10 text-center text-sm text-[#5A6377]">
            {query ? `No skills match “${query}”.` : 'No skills yet.'}
          </p>
        )}
        {shown.map((s) => {
          const editing = edit?.key === s.key;
          if (editing) {
            return (
              <div key={s.key} className="flex flex-wrap items-end gap-2 py-3">
                <div className="flex-1 min-w-[180px]">
                  <Label className={LBL}>Name</Label>
                  <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                </div>
                <div className="w-32">
                  <Label className={LBL}>Category</Label>
                  <Input value={edit.category || ''} onChange={(e) => setEdit({ ...edit, category: e.target.value })} />
                </div>
                {/* How this skill is assessed, changeable after the fact —
                    which of the two suits a skill is a judgement that gets
                    revised, and the alternative was deleting and recreating
                    it, which would break its mappings and unlabel its
                    history. Past assessments keep the method they were
                    taken under. */}
                <div className="w-36">
                  <Label className={LBL}>Assessed as</Label>
                  <select className="border border-[#E6E9F0] rounded-md text-sm px-2 py-2 w-full bg-white"
                          value={edit.method} data-testid={`skill-method-${s.key}`}
                          onChange={(e) => setEdit({ ...edit, method: e.target.value })}>
                    <option value="quad">Rating</option>
                    <option value="marks">Marking</option>
                  </select>
                </div>
                <div className="w-24">
                  <Label className={LBL}>Out of</Label>
                  {/* A Rating is scored out of the top of its scale, so the
                      maximum follows the scale rather than being typed. */}
                  <Input type="number" min="1" value={edit.max_marks}
                         disabled={edit.method === 'quad'}
                         title={edit.method === 'quad' ? 'Set by the rating scale' : undefined}
                         className={edit.method === 'quad' ? 'bg-[#F7F8FB] text-[#98A1B2]' : ''}
                         onChange={(e) => setEdit({ ...edit, max_marks: e.target.value })} />
                </div>
                <button onClick={() => run(() => skillMatrixApi.update(s.key, {
                          name: edit.name, method: edit.method,
                          category: edit.category || '',
                          // Only a Marking skill takes a typed maximum; the
                          // server refuses one on a Rating.
                          ...(edit.method === 'quad' ? {} : { max_marks: Number(edit.max_marks) }),
                        }), 'Saved').then(() => setEdit(null))}
                        className="p-2 text-[#15A36E]" data-testid={`skill-save-${s.key}`}><Check className="w-4 h-4" /></button>
                <button onClick={() => setEdit(null)} className="p-2 text-[#98A1B2]"><X className="w-4 h-4" /></button>
              </div>
            );
          }
          return (
            <div key={s.key} data-testid={`skill-row-${s.key}`}
                 className="group flex items-center gap-3 py-2.5 px-1 -mx-1 rounded-md hover:bg-[#F7F8FB]">
              <span className="min-w-0 flex-1">
                <span className={`text-sm ${s.active ? 'text-[#11182B]' : 'text-[#98A1B2] line-through'}`}>
                  {s.name}
                </span>
                <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                  s.method === 'quad' ? 'bg-[#EFF3FB] text-[#2F4A77] border-[#D9E2F3]'
                                      : 'bg-[#F3F0FB] text-[#5B3FA8] border-[#E0D8F5]'}`}>
                  {s.assessment_type || (s.method === 'quad' ? 'Rating' : 'Marking')}
                </span>
                <span className="ml-2 text-[11px] text-[#98A1B2]">
                  out of {s.max_marks}
                  {s.category ? ` · ${s.category}` : ''}
                  {s.assessment_count ? ` · ${s.assessment_count} assessment(s)` : ''}
                  {s.designation_count ? ` · ${s.designation_count} designation(s)` : ''}
                </span>
              </span>
              {!s.active && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#EFF1F6] text-[#5A6377] flex-none">
                  off
                </span>
              )}
              <div className="flex gap-1 flex-none opacity-0 group-hover:opacity-100 transition-opacity">
                <button title={`Rename ${s.name}`} data-testid={`skill-edit-${s.key}`}
                        onClick={() => setEdit({ key: s.key, name: s.name, max_marks: s.max_marks,
                                                 method: s.method || 'marks', category: s.category || '' })}
                        className="p-1 text-[#5A6377] hover:bg-[#EFF1F6] rounded"><Pencil className="w-3.5 h-3.5" /></button>
                <button title={s.active ? 'Switch off' : 'Switch on'} data-testid={`skill-toggle-${s.key}`}
                        onClick={() => run(() => skillMatrixApi.update(s.key, { active: !s.active }),
                                           s.active ? 'Switched off' : 'Switched on')}
                        className="p-1 text-[#15307F] hover:bg-[#E8EFFD] rounded text-[11px] font-semibold px-2">
                  {s.active ? 'Off' : 'On'}
                </button>
                <button title={`Remove ${s.name}`} data-testid={`skill-delete-${s.key}`}
                        onClick={() => remove(s)}
                        className="p-1 text-[#E11D2A] hover:bg-[#FDE7E9] rounded"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Designation → skills. Two lists, move between them.                 */
/* ------------------------------------------------------------------ */
export function DesignationSkillMapCard({ designation = null, embedded = false }) {
  const { hasPerm } = useAuth();
  const allowed = hasPerm('master.manage');
  // When opened from a designation's own row the job is already chosen, so
  // the picker is not shown and there is nothing to select twice.
  const fixed = !!designation;
  const [newSkill, setNewSkill] = useState(null);   // { name, method, max_marks }
  const [pool, setPool] = useState([]);
  const [map, setMap] = useState({});
  const [designations, setDesignations] = useState([]);
  const [picked, setPicked] = useState('');
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, m, d] = await Promise.all([
      skillMatrixApi.pool(), skillMatrixApi.map(), associateDesignationApi.list(),
    ]);
    setPool((p.data || []).filter((s) => s.active));
    const rows = m.data?.rows || [];
    setMap(Object.fromEntries(rows.map((r) => [r.designation, r.matrix_keys || []])));
    setDesignations((d.data || []).filter((x) => x.active && x.approved).map((x) => x.name));
  };
  useEffect(() => { if (allowed) load().catch(() => {}); }, [allowed]);
  useEffect(() => {
    if (fixed) setPicked(designation);
  }, [fixed, designation]);
  useEffect(() => {
    if (picked) setSelected(map[picked] || []);
  }, [picked, map]);
  // Above the permission return: hooks must run in the same order on every
  // render, and an early return before this one skips it.
  const byKey = useMemo(() => Object.fromEntries(pool.map((s) => [s.key, s])), [pool]);
  if (!allowed) return null;
  const available = pool.filter((s) => !selected.includes(s.key))
    .filter((s) => !query.trim() || s.name.toLowerCase().includes(query.trim().toLowerCase()));

  const save = async () => {
    setBusy(true);
    try {
      await skillMatrixApi.saveMap(picked, selected);
      toast.success(`${picked} now assesses ${selected.length} skill(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const Item = ({ s, onClick, side }) => (
    <button type="button" onClick={onClick} data-testid={`map-${side}-${s.key}`}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm
                       rounded-lg hover:bg-[#EEF2FE] text-[#11182B] transition-colors">
      <span className="truncate">{s.name}</span>
      <span className="text-[10px] text-[#98A1B2] flex-none">/{s.max_marks}</span>
      {side === 'available' ? <ChevronRight className="w-3.5 h-3.5 text-[#98A1B2] flex-none" />
                            : <ChevronLeft className="w-3.5 h-3.5 text-[#98A1B2] flex-none" />}
    </button>
  );

  return (
    <div className={embedded ? '' : CARD} data-testid="designation-skill-map-card">
      {!embedded && (
        <>
          <h2 className="text-[17px] font-semibold text-[#11182B] leading-tight">Designation Skills</h2>
          <p className="text-[12px] text-[#98A1B2] mt-0.5 mb-5">
            What each designation is assessed on
          </p>
        </>
      )}

      <div className={`mb-4 max-w-sm ${fixed ? 'hidden' : ''}`}>
        <Label className={LBL}>Designation</Label>
        <select className="border border-[#E6E9F0] rounded-md text-sm px-2 py-2 w-full bg-white"
                value={picked} onChange={(e) => setPicked(e.target.value)}
                data-testid="map-designation-select">
          <option value="">Pick a designation…</option>
          {designations.map((d) => (
            <option key={d} value={d}>{d} — {(map[d] || []).length} skill(s)</option>
          ))}
        </select>
      </div>

      {!picked ? (
        <p className="py-10 text-center text-sm text-[#98A1B2]">
          Pick a designation to see and change its skills.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[#E6E9F0] rounded-xl p-3 min-w-0">
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#98A1B2]">
                  Available ({available.length})
                </p>
                {/* Adds to the ONE central bank — never a designation-local
                    copy — and the new skill appears here immediately, ready
                    to assign without leaving the screen. */}
                <button type="button" data-testid="map-add-skill"
                        onClick={() => setNewSkill({ name: '', method: 'quad', max_marks: 4 })}
                        className="text-[11px] font-semibold text-[#15307F] hover:underline">
                  + Add skill
                </button>
              </div>
              {newSkill && (
                <div className="mb-2 p-3 rounded-lg bg-[#F7F8FB] border border-[#EDF0F5] space-y-2">
                  <Input autoFocus value={newSkill.name} data-testid="map-new-skill-name"
                         placeholder="Skill name"
                         onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })} />
                  <div className="flex gap-2">
                    <select className="border border-[#E6E9F0] rounded-md text-sm px-2 py-1.5 flex-1 bg-white"
                            value={newSkill.method} data-testid="map-new-skill-method"
                            onChange={(e) => setNewSkill({ ...newSkill, method: e.target.value })}>
                      <option value="quad">Rating</option>
                      <option value="marks">Marking</option>
                    </select>
                    {newSkill.method === 'marks' && (
                      <Input type="number" min="1" className="w-24" value={newSkill.max_marks}
                             data-testid="map-new-skill-max"
                             onChange={(e) => setNewSkill({ ...newSkill, max_marks: e.target.value })} />
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button className="text-xs text-[#5A6377]" onClick={() => setNewSkill(null)}>Cancel</button>
                    <Button size="sm" data-testid="map-new-skill-save" onClick={async () => {
                      if (!newSkill.name.trim()) { toast.error('Skill name is required'); return; }
                      try {
                        const r = await skillMatrixApi.create({
                          name: newSkill.name.trim(), method: newSkill.method,
                          max_marks: newSkill.method === 'quad' ? 4 : Number(newSkill.max_marks || 0),
                        });
                        toast.success('Added to Skills Bank');
                        await load();
                        // Assigned to the designation already open, so the
                        // admin does not have to find it again.
                        setSelected((cur) => [...cur, r.data.key]);
                        setNewSkill(null);
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
                    }}>Add</Button>
                  </div>
                </div>
              )}
              <Input className="h-9 mb-2" value={query} placeholder="Search…"
                     onChange={(e) => setQuery(e.target.value)} data-testid="map-search" />
              <div className="max-h-[300px] overflow-y-auto">
                {!available.length && (
                  <p className="text-xs text-[#98A1B2] py-6 text-center">
                    {query ? 'No match.' : 'Every skill is already selected.'}
                  </p>
                )}
                {available.map((s) => (
                  <Item key={s.key} s={s} side="available"
                        onClick={() => setSelected([...selected, s.key])} />
                ))}
              </div>
            </div>

            <div className="border border-[#E6E9F0] rounded-xl p-3 min-w-0 bg-[#FBFCFE]">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#98A1B2] mb-2">
                Assessed on ({selected.length})
              </p>
              <div className="max-h-[341px] overflow-y-auto">
                {!selected.length && (
                  <p className="text-xs text-[#98A1B2] py-6 text-center">
                    Nothing selected — anybody with this designation cannot be assessed.
                  </p>
                )}
                {selected.map((k) => {
                  const s = byKey[k];
                  // A key with no live skill behind it is a skill that was
                  // switched off after being mapped. Shown rather than
                  // dropped, so it can be removed deliberately.
                  if (!s) {
                    return (
                      <div key={k} className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-[#98A1B2]">
                        <span className="truncate italic">{k} (switched off)</span>
                        <button onClick={() => setSelected(selected.filter((x) => x !== k))}
                                className="text-[#E11D2A] text-xs">remove</button>
                      </div>
                    );
                  }
                  return (
                    <Item key={k} s={s} side="selected"
                          onClick={() => setSelected(selected.filter((x) => x !== k))} />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Button size="sm" onClick={save} disabled={busy} data-testid="map-save">Save mapping</Button>
            <button className="text-xs text-[#5A6377] hover:underline"
                    onClick={() => setSelected(pool.map((s) => s.key))}>Select all</button>
            <button className="text-xs text-[#5A6377] hover:underline"
                    onClick={() => setSelected(map[picked] || [])}>Reset</button>
            <span className="text-xs text-[#98A1B2] ml-auto">
              Changing this does not alter any assessment already saved.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
